import React, { useState } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useAddCustomerComment, useGetCustomerComments } from '../../services/hooks/customerCommentHooks';
import type { CustomerComment } from '../../services/schemas/customerServiceSchemas/customerComment';
import { AddCustomerCommentContext } from '../../services/schemas/customerServiceSchemas/addCustomerCommentContext';
import { formatDate } from '../../utils/formatters';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const CommentContext = AddCustomerCommentContext;
type CommentContext = AddCustomerCommentContext;

interface CustomerCommentsProps {
  customerId: string;
  className?: string;
}

const CONTEXT_OPTIONS: { value: CommentContext; label: string }[] = [
  { value: 'GENERAL', label: 'General' },
  { value: 'KYC_REVIEW', label: 'KYC Review' },
  { value: 'COMPLAINT', label: 'Complaint' },
  { value: 'FOLLOW_UP', label: 'Follow Up' },
  { value: 'ACCOUNT_UPDATE', label: 'Account Update' },
];

const getContextBadgeColor = (context: string): string => {
  const colors: Record<string, string> = {
    GENERAL: 'secondary',
    KYC_REVIEW: 'info',
    COMPLAINT: 'danger',
    FOLLOW_UP: 'warning',
    ACCOUNT_UPDATE: 'primary',
  };
  return colors[context] || 'secondary';
};

const columns: TableColumn<CustomerComment>[] = [
  {
    name: 'Date',
    selector: (row) => row.createdDate || '',
    cell: (row) => (
      <span className="text-muted small">{formatDate(row.createdDate || '')}</span>
    ),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Created By',
    selector: (row) => row.createdBy || '',
    cell: (row) => (
      <div className="d-flex align-items-center">
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2 flex-shrink-0"
          style={{ width: '28px', height: '28px', fontSize: '12px' }}
        >
          <i className="fa fa-user"></i>
        </div>
        <span className="fw-semibold">{row.createdBy}</span>
      </div>
    ),
    sortable: true,
    width: '180px',
  },
  {
    name: 'Context',
    selector: (row) => row.context || '',
    cell: (row) => (
      <span className={`badge bg-${getContextBadgeColor(row.context || '')} text-white`}>
        {row.context?.replace(/_/g, ' ')}
      </span>
    ),
    sortable: true,
    width: '150px',
  },
  {
    name: 'Comment',
    selector: (row) => row.comment || '',
    cell: (row) => (
      <div className="py-2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {row.comment}
      </div>
    ),
    grow: 2,
    wrap: true,
  },
];

const CustomerComments: React.FC<CustomerCommentsProps> = ({ customerId, className = '' }) => {
  const [newComment, setNewComment] = useState('');
  const [selectedContext, setSelectedContext] = useState<CommentContext>('GENERAL');
  const [filterContext, setFilterContext] = useState<CommentContext | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const user = useSelector((state: RootState) => state.auth.user);

  // Fetch comments (server-side pagination)
  const {
    data: commentsPage,
    isLoading,
    refetch: refetchComments,
  } = useGetCustomerComments(
    customerId,
    { context: filterContext || undefined, page, size: pageSize },
    { enabled: !!customerId },
  );

  const comments: CustomerComment[] = commentsPage?.content ?? [];
  const totalRows = commentsPage?.totalElements ?? 0;

  // Add comment mutation
  const addCommentMutation = useAddCustomerComment();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      await addCommentMutation.mutateAsync({
        customerId,
        comment: newComment.trim(),
        context: selectedContext,
        createdBy: user?.name || user?.username || undefined,
      });
      setNewComment('');
      toast.success('Comment added successfully');
      setPage(0);
      refetchComments();
    } catch (error: unknown) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1); // DataTable is 1-indexed, API is 0-indexed
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPageSize(newPerPage);
    setPage(0);
  };

  return (
    <div className={`customer-comments ${className}`}>
      {/* Add Comment Form */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h6 className="fw-bold mb-3">
            <i className="fa fa-plus-circle me-2 text-primary"></i>
            Add Comment
          </h6>
          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold mb-2">Context</label>
                <select
                  className="form-select"
                  value={selectedContext}
                  onChange={(e) => setSelectedContext(e.target.value as CommentContext)}
                  disabled={isSubmitting}
                >
                  {CONTEXT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2">Comment</label>
              <textarea
                className="form-control"
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Enter your comment here..."
                disabled={isSubmitting}
                maxLength={2000}
              />
              <small className="text-muted">{newComment.length}/2000 characters</small>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isSubmitting || !newComment.trim()}
            >
              {isSubmitting ? (
                <React.Fragment>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding...
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <i className="fa fa-plus me-2"></i>
                  Add Comment
                </React.Fragment>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Comments Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0 py-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-bold">
            <i className="fa fa-comments me-2 text-primary"></i>
            Comments &amp; Notes
          </h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small me-1">Filter:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterContext}
              onChange={(e) => {
                setFilterContext(e.target.value as CommentContext | '');
                setPage(0);
              }}
            >
              <option value="">All Contexts</option>
              {CONTEXT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <DataTable
              columns={columns}
              data={comments}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page + 1}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponentOptions={{
                rowsPerPageText: 'Rows per page:',
                rangeSeparatorText: 'of',
              }}
              highlightOnHover
              striped
              responsive
              progressPending={isLoading}
              progressComponent={
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading comments...</span>
                  </div>
                  <p className="mt-2 text-muted mb-0">Loading comments...</p>
                </div>
              }
              noDataComponent={
                <div className="text-center py-4">
                  <i className="fa fa-comments fa-2x text-muted mb-2 d-block"></i>
                  <p className="text-muted mb-0">
                    {filterContext
                      ? `No comments found for context: ${filterContext.replace(/_/g, ' ')}`
                      : 'No comments yet. Be the first to add a comment!'}
                  </p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerComments;